import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import _ from "lodash"
import { Sidebar } from "primereact/sidebar"

import Menu from "../../helpers/Menu"
import { Button, Table } from "../../helpers"

import Loading from "../../common/Loading"
import PMCompanyName from "../../common/PMCompanyName"

import Search from "./Components/Search"
import AddMaterial from "./Components/AddMaterial"
import UploadVMIMaterialSheet from "./Components/UploadVMIMaterialSheet"

import { getADHOCCompanies } from "../../services/api/company"

import { raiseToast } from "../../services/redux/reducers/toast/toast"
import { fetchVMIMaterials } from "../../services/redux/reducers/vmi/vmi"
import { fetchCompanyWarehouses, fetchMaterialBrands, fetchVMIQtyTypes } from "../../services/redux/reducers/commonThunk"

import { isMobile, sortArrayOfObjectsInAlphabeticalOrder } from "../../utils"
import { useSearchPin } from "../../hooks/useSearchPin";


const VMI = () => {
    const dispatch = useDispatch()
    const { isSearchPinned, toggleSearchPin, setSearchPin } = useSearchPin()

    const [isSearchVisible, setIsSearchVisible] = useState(true)
    const [visibleAddMaterial, setVisibleAddMaterial] = useState(false)
    const [visibleUploadVMIMaterialSheet, setVisibleUploadVMIMaterialSheet] = useState(false)
    const [companies, setCompanies] = useState([])
    const [allCompanies, setAllCompanies] = useState([])

    const [isLoading, setIsLoading] = useState(false)
    const [isHoveringSearchZone, setIsHoveringSearchZone] = useState(false)
    const [hasEverHoveredRightEdge, setHasEverHoveredRightEdge] = useState(false)

    const isMobileDevice = isMobile()

    const actionMenu = useRef(null)

    const userDetails = useSelector((state) => state.userDetails)
    const companyWarehouses = useSelector(state => state.common.companyWarehouses)
    const activeRoleId = useSelector((state) => state.userDetails.activeRoleId)
    const materialBrands = useSelector((state) => state.common.materialBrands)
    const vmiQtyTypes = useSelector((state) => state.common.vmiQtyTypes)

    const loading = useSelector(state => state.vmiMaterials.loading)
    const paginationParams = useSelector(state => state.vmiMaterials.paginationParams)
    const searchParams = useSelector(state => state.vmiMaterials.searchParams)
    const vmiMaterials = useSelector(state => state.vmiMaterials.vmiMaterials)


    useEffect(() => {
        dispatch(fetchCompanyWarehouses())
        dispatch(fetchMaterialBrands())
        dispatch(fetchVMIQtyTypes())
    }, [])

    useEffect(() => {
        if (!isSearchPinned && !isHoveringSearchZone) {
            setIsSearchVisible(false)
          }

        const handleMouseMove = (e) => {
            if (isSearchPinned) return

            const panelWidth = window.innerWidth * 0.18
            const buffer = 8
            const screenRightEdge = window.innerWidth
            const activeZoneLeft = screenRightEdge - (panelWidth + buffer)

            if (e.clientX < activeZoneLeft) {
                setIsHoveringSearchZone(false)
                setHasEverHoveredRightEdge(false)
            } else {
                setIsHoveringSearchZone(true)
            }
        }

        if (!isSearchPinned && (isHoveringSearchZone || hasEverHoveredRightEdge)) {
            window.addEventListener('mousemove', handleMouseMove)
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [isSearchPinned, isHoveringSearchZone, hasEverHoveredRightEdge])

    const actionMenuItems = [
        {
            label: 'Add VMI Material',
            icon: '',
            command: async () => {
                await handleAddVMIProduct()
                setVisibleAddMaterial(true)
            },
            permissions: [232]
        },
        {
            label: 'Upload VMI Materials Sheet',
            icon: '',
            command: async () => { 
                await handleAddVMIProduct()
                setVisibleUploadVMIMaterialSheet(true)
            },
            permissions: [231]
        },
        // {
        //     label: 'Download VMI Materials Sample Sheet',
        //     icon: '',
        //     command: () => { }
        // }
    ]

    const getWarehouseNameBody = (row) => {
        let relatedWarehouse = {}
        if (row.warehouseId){
            relatedWarehouse = companyWarehouses.find(eachCompanyWarehouse => eachCompanyWarehouse.warehouseId === row.warehouseId)
        }

        return <>{relatedWarehouse.warehouseName}</>
    }

    const getOrderQtyBody = (row) => {
        return (row.VMIQtyTypeId === 1 ? <>{row.VMIQty}</> : "-")
    }

    const getTargetQtyBody = (row) => {
        return (row.VMIQtyTypeId === 2 ? <>{row.VMIQty}</> : "-")
    }

    const getCompanyBody = (row)=>{
        let companyName = row.companyName
        let companyId = row.companyId

        return <PMCompanyName companyName={companyName} companyId={companyId}/>
    }

    const getProductBody = (row) => {
        return (
            <>
                {row.productName} | {row.productSpecification}
            </>
        )
    }

    const tableColumns = [
        {
            header: 'Company Name',
            style: { minWidth: "220px" },
            body: getCompanyBody
        },
        {
            header: 'Warehouse Name',
            style: { minWidth: "150px" },
            body: getWarehouseNameBody
        },
        {
            header: 'Company ERP Code',
            style: { minWidth: "150px" },
            field: "companyERPCode",
        },
        {
            header: 'Perm Material Id',
            field: 'permMaterialId',
            style: { minWidth: "100px" }
        },
        {
            header: 'VMI Material Id',
            field: 'materialId',
            style: { minWidth: "100px" }

        },
        {
            header: 'Product Name & Specs',
            body: getProductBody,
            style: { minWidth: "220px" }
        },
        {
            header: 'Ordered Thershold Qty',
            style: { minWidth: "90px"},
            field: 'orderingThresholdQty'
        },
        {
            header: 'Safety Stock',
            style: { minWidth: "90px" },
            field: 'safetyStock'
        },
        {
            header: 'Order Qty',
            body: getOrderQtyBody,
            style: { minWidth: "90px" }
        },
        {
            header: 'Target Stock Qty',
            body: getTargetQtyBody,
            style: { minWidth: "90px" }
        }
    ]

    const handleAddVMIProduct = async () => {
        setIsLoading(true)
        const activeUserRole = userDetails.rolesList.find(eachRole => eachRole.userRoleId === activeRoleId)
        const companyData = { prefPMBranchIds: [activeUserRole?.branchId ? activeUserRole.branchId : 1], buyerSeller: [1, 3] }
        const companyRes = await getADHOCCompanies({ ...companyData })
        if (companyRes.failure) {
            dispatch(raiseToast({ severity: 'error', summary: 'Company', detail: companyRes.data.error, life: 3000 }))

            setCompanies([])
            setIsLoading(false)
        }
        else {
            let updatedCompanyData = _.uniqBy(companyRes.data.data, "companyId")
            updatedCompanyData = sortArrayOfObjectsInAlphabeticalOrder(updatedCompanyData, 'companyName')

            setCompanies(updatedCompanyData)
            setAllCompanies(companyRes.data.data)
            setIsLoading(false)
        }
    }

    const handlePageChange = async (e) => {
        let VMIParams = {
            ...searchParams,
            paginator: true,
            pageNo: e.page + 1,
            recordsPerPage: e.rows,
            limitedPagination: true,
            getCount: false
        }

        if (VMIParams.searchBy !== "all") {
            VMIParams[VMIParams.searchBy] = VMIParams.searchText.trim()
        }

        dispatch(fetchVMIMaterials({ ...VMIParams }))
    }

    const actionButtons = [
        {
            icon: "pi pi-filter",
            label: "Filter",
            command: () => setIsSearchVisible(!isSearchVisible),
            className: isSearchVisible ? "active-group-button" : ""
        },
        {
            icon: isSearchPinned ? "pi pi-lock" : "pi pi-lock-open",
            command: () => toggleSearchPin() 
        }
    ]

    let displaySearchBox = isSearchPinned || isHoveringSearchZone || isSearchVisible


    return (
        <div>
            <Loading visible={isLoading} />
            <div
                onMouseEnter={() => {
                    if (!isSearchPinned) {
                        setIsHoveringSearchZone(true)
                        setHasEverHoveredRightEdge(true)
                        setIsSearchVisible(true)
                    }
                }}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '8px',
                    height: '100vh',
                    zIndex: 999
                }}
            />
            <div className="flex justify-content-between">
                <h4 className="m-2">VMI</h4>
                <div className="flex gap-2 justify-content-end mb-2">
                    <Menu model={actionMenuItems} popup menuRef={actionMenu} />
                    <Button className="h-2rem p-2 action-btn hover:surface-400 hover:text-700 surface-300" label="Action" onClick={(e) => actionMenu.current.toggle(e)} />
                    <Button icon="pi pi-filter" className="h-2rem  p-2 action-btn hover:surface-400 hover:text-700 surface-300" label="Filter" onClick={() => { setIsSearchVisible(!isSearchVisible) }} />
                </div>
            </div>
            <div className='flex flex-row'>
                <div className="approval-instances-table" style={{ order: "1", width: displaySearchBox && !isMobileDevice ? "82%" : "100%", maxHeight: "'calc(100%-100px)'" }}>
                    <div className='p-card mb-0' >
                        <div className="flex-grow-1" >
                            <Table
                                lazy
                                page={paginationParams.pageNo}
                                onPageChange={(e) => handlePageChange(e)}
                                totalRecords={paginationParams.totalRecords}
                                tableColumns={tableColumns}
                                data={vmiMaterials}
                                rows={paginationParams.recordsPerPage}
                                scrollable={"true"}
                                scrollHeight="550px"
                                paginator
                                excessPagesFound={paginationParams.excessPagesFound}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>

                {displaySearchBox && (
                    <div
                        style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end"
                        onMouseLeave={(e) => {
                            const bounds = e.currentTarget.getBoundingClientRect()
                            const leftExit = e.clientX < bounds.left
                            const topExit = e.clientY < bounds.top
                            const bottomExit = e.clientY > bounds.bottom

                            if (!isSearchPinned && (leftExit || topExit || bottomExit)) {
                                setIsHoveringSearchZone(false)
                                setHasEverHoveredRightEdge(false)
                            }
                        }}

                        onMouseEnter={() => {
                            if (!isSearchPinned) {
                                setIsHoveringSearchZone(true)
                            }
                        }}
                    >
                        <Search />
                    </div>
                )}
            </div>
            <AddMaterial
                visible={visibleAddMaterial}
                setVisibleAddMaterial={setVisibleAddMaterial}
                companyWarehouses={companyWarehouses}
                companies={companies}
                allCompanies={allCompanies}
                setIsLoading={setIsLoading}
                userDetails={userDetails}
                materialBrands={materialBrands}
            />
            <UploadVMIMaterialSheet 
                visible={visibleUploadVMIMaterialSheet}
                setVisibleUploadVMIMaterialSheet={setVisibleUploadVMIMaterialSheet}
                companies={companies}
                allCompanies={allCompanies}
                setIsLoading={setIsLoading}
                companyWarehouses={companyWarehouses}
                vmiQtyTypes={vmiQtyTypes}
            />
        </div>
    )
}

export default VMI